/**
 * Colour grade for a PropertyCuts video clip: a Photoshop-style curve
 * (used to brighten) plus temperature and tint (white balance).
 *
 * The preview and the export must match, so both consume `gradeChannelLuts`.
 * The browser paints that LUT with an SVG colour filter. The worker writes
 * the same LUT as a 1D .cube and ffmpeg's `lut1d` bakes it into the file
 * Shotstack receives. White balance is applied first, then the master curve,
 * then the red, green and blue curves.
 */

export interface CurvePoint {
  /** Input, 0–255. */
  x: number;
  /** Output, 0–255. */
  y: number;
}

export type CurveChannel = 'rgb' | 'r' | 'g' | 'b';

export interface ClipGrade {
  rgb: CurvePoint[];
  r: CurvePoint[];
  g: CurvePoint[];
  b: CurvePoint[];
  /** -100..100. Positive is warmer. */
  temperature: number;
  /** -100..100. Positive is magenta, negative is green. */
  tint: number;
}

export const IDENTITY_CURVE: CurvePoint[] = [
  { x: 0, y: 0 },
  { x: 255, y: 255 },
];

/** A gentle lift through the shadows and midtones, holding the whites. */
export const BRIGHTEN_CURVE: CurvePoint[] = [
  { x: 0, y: 0 },
  { x: 48, y: 78 },
  { x: 128, y: 176 },
  { x: 200, y: 226 },
  { x: 255, y: 255 },
];

const MAX_CURVE_POINTS = 8;

function clamp(n: number, min: number, max: number): number {
  return n < min ? min : n > max ? max : n;
}

export function identityClipGrade(): ClipGrade {
  return {
    rgb: IDENTITY_CURVE.map((p) => ({ ...p })),
    r: IDENTITY_CURVE.map((p) => ({ ...p })),
    g: IDENTITY_CURVE.map((p) => ({ ...p })),
    b: IDENTITY_CURVE.map((p) => ({ ...p })),
    temperature: 0,
    tint: 0,
  };
}

function isIdentityCurve(points: CurvePoint[]): boolean {
  return points.length === 2 && points[0].x === 0 && points[0].y === 0 && points[1].x === 255 && points[1].y === 255;
}

export function isIdentityGrade(grade: ClipGrade): boolean {
  return (
    grade.temperature === 0 &&
    grade.tint === 0 &&
    isIdentityCurve(grade.rgb) &&
    isIdentityCurve(grade.r) &&
    isIdentityCurve(grade.g) &&
    isIdentityCurve(grade.b)
  );
}

function finite(value: unknown): number | null {
  const n = typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : NaN;
  return Number.isFinite(n) ? n : null;
}

/** Sort, clamp and cap control points. Missing or empty input is the identity line. */
export function normaliseCurve(value: unknown): CurvePoint[] {
  if (!Array.isArray(value)) return IDENTITY_CURVE.map((p) => ({ ...p }));
  const sorted: CurvePoint[] = [];
  for (const item of value) {
    if (!item || typeof item !== 'object') continue;
    const x = finite((item as CurvePoint).x);
    const y = finite((item as CurvePoint).y);
    if (x == null || y == null) continue;
    const point = { x: clamp(Math.round(x), 0, 255), y: clamp(Math.round(y), 0, 255) };
    const last = sorted[sorted.length - 1];
    if (last && last.x === point.x) sorted[sorted.length - 1] = point;
    else sorted.push(point);
  }
  sorted.sort((a, b) => a.x - b.x);
  const deduped: CurvePoint[] = [];
  for (const point of sorted) {
    if (deduped.length && deduped[deduped.length - 1].x === point.x) deduped[deduped.length - 1] = point;
    else deduped.push(point);
  }
  if (deduped.length === 0) return IDENTITY_CURVE.map((p) => ({ ...p }));
  if (deduped[0].x !== 0) deduped.unshift({ x: 0, y: 0 });
  if (deduped[deduped.length - 1].x !== 255) deduped.push({ x: 255, y: 255 });
  if (deduped.length <= MAX_CURVE_POINTS) return deduped;
  const interior = deduped.slice(1, -1).slice(0, MAX_CURVE_POINTS - 2);
  return [deduped[0], ...interior, deduped[deduped.length - 1]];
}

export function normaliseClipGrade(value: unknown): ClipGrade | null {
  if (!value || typeof value !== 'object') return null;
  const raw = value as Partial<ClipGrade>;
  return {
    rgb: normaliseCurve(raw.rgb),
    r: normaliseCurve(raw.r),
    g: normaliseCurve(raw.g),
    b: normaliseCurve(raw.b),
    temperature: clamp(Math.round(finite(raw.temperature) ?? 0), -100, 100),
    tint: clamp(Math.round(finite(raw.tint) ?? 0), -100, 100),
  };
}

/** Grade to store and render, or undefined when it would not change the picture. */
export function activeClipGrade(value: unknown): ClipGrade | undefined {
  const grade = normaliseClipGrade(value);
  if (!grade || isIdentityGrade(grade)) return undefined;
  return grade;
}

export function clipGradeOrIdentity(value: unknown): ClipGrade {
  return normaliseClipGrade(value) ?? identityClipGrade();
}

/**
 * Natural cubic spline through the control points, sampled at every
 * input level. Same construction PhotoEdit uses for stills.
 */
export function buildLut(pointsInput: CurvePoint[]): Uint8Array {
  const lut = new Uint8Array(256);
  const points = normaliseCurve(pointsInput);
  if (points.length === 1) {
    lut.fill(clamp(Math.round(points[0].y), 0, 255));
    return lut;
  }
  const n = points.length;
  const xs = points.map((p) => p.x);
  const ys = points.map((p) => p.y);
  const y2 = new Array<number>(n).fill(0);
  const u = new Array<number>(n).fill(0);
  for (let i = 1; i < n - 1; i++) {
    const sig = (xs[i] - xs[i - 1]) / (xs[i + 1] - xs[i - 1]);
    const p = sig * y2[i - 1] + 2;
    y2[i] = (sig - 1) / p;
    let du =
      (ys[i + 1] - ys[i]) / (xs[i + 1] - xs[i]) - (ys[i] - ys[i - 1]) / (xs[i] - xs[i - 1]);
    du = (6 * du) / (xs[i + 1] - xs[i - 1]) - sig * u[i - 1];
    u[i] = du / p;
  }
  for (let i = n - 2; i >= 0; i--) y2[i] = y2[i] * y2[i + 1] + u[i];

  const splineAt = (x: number): number => {
    let klo = 0;
    let khi = n - 1;
    while (khi - klo > 1) {
      const k = (khi + klo) >> 1;
      if (xs[k] > x) khi = k;
      else klo = k;
    }
    const h = xs[khi] - xs[klo];
    if (h === 0) return ys[klo];
    const a = (xs[khi] - x) / h;
    const b = (x - xs[klo]) / h;
    return a * ys[klo] + b * ys[khi] + ((a * a * a - a) * y2[klo] + (b * b * b - b) * y2[khi]) * (h * h) / 6;
  };

  for (let i = 0; i < 256; i++) {
    let v: number;
    if (i <= xs[0]) v = ys[0];
    else if (i >= xs[n - 1]) v = ys[n - 1];
    else v = splineAt(i);
    lut[i] = clamp(Math.round(v), 0, 255);
  }
  return lut;
}

export function curveSamples(points: CurvePoint[], steps = 48): CurvePoint[] {
  const lut = buildLut(points);
  const out: CurvePoint[] = [];
  const count = Math.max(2, steps);
  for (let i = 0; i < count; i++) {
    const x = Math.round((i / (count - 1)) * 255);
    out.push({ x, y: lut[x] });
  }
  return out;
}

/** Channel gains matching PhotoEdit's temperature / tint sliders. */
export function whiteBalanceGains(temperature: number, tint: number): { gainR: number; gainG: number; gainB: number } {
  const t = clamp(temperature, -100, 100) / 100;
  const n = clamp(tint, -100, 100) / 100;
  return {
    gainR: 1 + t * 0.3 + n * 0.12,
    gainG: 1 - n * 0.24,
    gainB: 1 - t * 0.3 + n * 0.12,
  };
}

export function gradeChannelLuts(grade: ClipGrade): { r: Uint8Array; g: Uint8Array; b: Uint8Array } {
  const master = buildLut(grade.rgb);
  const red = buildLut(grade.r);
  const green = buildLut(grade.g);
  const blue = buildLut(grade.b);
  const { gainR, gainG, gainB } = whiteBalanceGains(grade.temperature, grade.tint);
  const apply = (gain: number, channel: Uint8Array) => {
    const out = new Uint8Array(256);
    for (let i = 0; i < 256; i++) {
      const lifted = clamp(Math.round(i * gain), 0, 255);
      out[i] = channel[master[lifted]];
    }
    return out;
  };
  return { r: apply(gainR, red), g: apply(gainG, green), b: apply(gainB, blue) };
}

function lutLine(lut: Uint8Array): string {
  let line = '';
  for (let i = 0; i < lut.length; i++) {
    if (i) line += ' ';
    line += (lut[i] / 255).toFixed(5);
  }
  return line;
}

/** SVG `feFunc*` tableValues. 256 stops, so each input level maps exactly. */
export function gradeFilterTables(grade: ClipGrade): { r: string; g: string; b: string } {
  const luts = gradeChannelLuts(grade);
  return { r: lutLine(luts.r), g: lutLine(luts.g), b: lutLine(luts.b) };
}

/** ffmpeg `lut1d` cube. One RGB output per input level, same numbers as the preview. */
export function gradeTo1dCube(grade: ClipGrade): string {
  const luts = gradeChannelLuts(grade);
  const lines = ['LUT_1D_SIZE 256'];
  for (let i = 0; i < 256; i++) {
    lines.push(`${(luts.r[i] / 255).toFixed(6)} ${(luts.g[i] / 255).toFixed(6)} ${(luts.b[i] / 255).toFixed(6)}`);
  }
  return lines.join('\n') + '\n';
}

function clamp100(n: number): number {
  return clamp(Math.round(n), -100, 100);
}

/** Temperature and tint that neutralise one sampled colour. */
export function whiteBalanceForRgb(r: number, g: number, b: number): { temperature: number; tint: number } {
  if (r <= 0 || g <= 0 || b <= 0) return { temperature: 0, tint: 0 };
  const grey = (r + g + b) / 3;
  const gainR = grey / r;
  const gainG = grey / g;
  const gainB = grey / b;
  return {
    temperature: clamp100(((gainR - gainB) / 0.6) * 100),
    tint: clamp100(((1 - gainG) / 0.24) * 100),
  };
}

/**
 * Grey-world white balance from a frame. Near-black and near-white pixels
 * are skipped so windows and shadows do not dominate the average.
 */
export function autoWhiteBalanceFromImageData(data: Uint8ClampedArray): { temperature: number; tint: number } {
  let sumR = 0;
  let sumG = 0;
  let sumB = 0;
  let n = 0;
  for (let i = 0; i < data.length; i += 4) {
    const lum = 0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2];
    if (lum < 12 || lum > 243) continue;
    sumR += data[i];
    sumG += data[i + 1];
    sumB += data[i + 2];
    n++;
  }
  if (n === 0) return { temperature: 0, tint: 0 };
  return whiteBalanceForRgb(sumR / n, sumG / n, sumB / n);
}

/** Average a small neighbourhood of a frame. `x` and `y` are 0–1. */
export function averagePatch(
  data: Uint8ClampedArray,
  width: number,
  height: number,
  x: number,
  y: number,
): { r: number; g: number; b: number } {
  const cx = clamp(Math.round(x * (width - 1)), 0, width - 1);
  const cy = clamp(Math.round(y * (height - 1)), 0, height - 1);
  const rad = 4;
  let r = 0;
  let g = 0;
  let b = 0;
  let n = 0;
  for (let yy = cy - rad; yy <= cy + rad; yy++) {
    if (yy < 0 || yy >= height) continue;
    for (let xx = cx - rad; xx <= cx + rad; xx++) {
      if (xx < 0 || xx >= width) continue;
      const i = (yy * width + xx) * 4;
      r += data[i];
      g += data[i + 1];
      b += data[i + 2];
      n++;
    }
  }
  if (n === 0) return { r: 0, g: 0, b: 0 };
  return { r: r / n, g: g / n, b: b / n };
}

/**
 * Map a click on a video element to a point in the source frame.
 * `positionX` / `positionY` are CSS object-position as 0–1.
 * Returns null when the click lands on a letterbox bar.
 */
export function videoSourcePoint(args: {
  clickX: number;
  clickY: number;
  elementWidth: number;
  elementHeight: number;
  videoWidth: number;
  videoHeight: number;
  fit: 'cover' | 'contain';
  positionX: number;
  positionY: number;
}): { x: number; y: number } | null {
  const { elementWidth: ew, elementHeight: eh, videoWidth: vw, videoHeight: vh } = args;
  if (ew <= 0 || eh <= 0 || vw <= 0 || vh <= 0) return null;
  const scale = args.fit === 'cover' ? Math.max(ew / vw, eh / vh) : Math.min(ew / vw, eh / vh);
  const dw = vw * scale;
  const dh = vh * scale;
  const pictureLeft = (ew - dw) * clamp(args.positionX, 0, 1);
  const pictureTop = (eh - dh) * clamp(args.positionY, 0, 1);
  const localX = args.clickX - pictureLeft;
  const localY = args.clickY - pictureTop;
  if (localX < -0.5 || localY < -0.5 || localX > dw + 0.5 || localY > dh + 0.5) return null;
  return { x: clamp(localX / dw, 0, 1), y: clamp(localY / dh, 0, 1) };
}
